import type { FC, ReactNode } from "react";

import styled from "styled-components";

import { icons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { DropdownItem, EllipsisDropdownItem } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { SETUP_BACKGROUND_IMAGE } from "app/pages/activation/configs";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TabFilter } from "./monitorKeywordsStartPageTypes";

const { carbon } = colorsPalettes;

export const SearchInput = styled.input.attrs({ type: "search" })`
    && {
        ${setFontVariant("input2")};
        border: 1px solid ${carbon[50]};
        border-radius: 3px;
        padding: 9px 9px 9px 34px;
        position: relative;
        background-image: url("data:image/svg+xml;base64,${window.btoa(icons.search)}");
        background-size: 22px;
        background-repeat: no-repeat;
        background-position: 8px center;
        box-sizing: content-box;
        box-shadow: none;
        outline: none;
        margin: 0;

        &::placeholder {
            color: ${carbon[250]};
        }

        &:hover {
            border-color: ${carbon[100]};
        }

        &:focus,
        &:active {
            border-color: ${colorsPalettes.blue[400]};
        }
    }
`;

export const TableCellBoldText = (value: ReactNode): ReactNode => {
    return (
        <Text
            fontVariant="subtitle2"
            color={carbon[500]}
        >
            {value}
        </Text>
    );
};

export const EllipsisDropdownItemStyled = styled(EllipsisDropdownItem)`
    svg {
        height: 100%;
        width: 100%;
        fill: ${carbon[200]};
    }
`;

const BGImage = AssetsService.assetUrl(SETUP_BACKGROUND_IMAGE);

export const KeywordGroupsPageContainer = styled.div`
    background-image: url("${BGImage}");
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    overflow: auto;
    height: 100%;
    padding: 80px 40px;
    box-sizing: border-box;
`;

export const KeywordGroupsPageContentContainer = styled.div`
    max-width: 1366px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const Tag = styled.span`
    ${setFontVariant("tag")};
    background-color: ${carbon[50]};
    letter-spacing: 0.6px;
    border-radius: 16px;
    padding: 4px 5px;
    text-transform: uppercase;
    box-sizing: border-box;
    height: 16px;
`;

const ImageContainer = styled.div<{ height?: number; width?: number; img: string }>`
    background-image: url("${({ img }) => img}");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    overflow: auto;
    height: ${({ height = 200 }) => `${height}px`};
    width: ${({ width = 200 }) => `${width}px`};
`;

const KEYWORDS_EMPTY = AssetsService.assetUrl(`/images/keywords/keyword-groups-empty.svg`);
export const KeywordsEmptyImage = () => (
    <ImageContainer
        img={KEYWORDS_EMPTY}
        width={464}
        height={358}
    />
);

const NOT_FOUND_CACTUS = AssetsService.assetUrl(`/images/ImpersonateAuth/cactus.svg`);
export const NotFoundImage = () => <ImageContainer img={NOT_FOUND_CACTUS} />;

export const NoDataTableTemplate: FC<{ tab: number; filterActive: boolean }> = ({ tab, filterActive }) => {
    const getHeaderText = () => {
        if (filterActive) {
            return translate("global.noMatchingResults");
        }

        if (tab === TabFilter.MY) {
            return translate("keyword.groups.noData.personal");
        }

        return translate("workspaces.marketing.sidenav.keywordgroups.sharedlists.empty");
    };

    const getDescriptionText = () => {
        if (filterActive) {
            return translate("keyword.groups.filter.noData");
        }

        if (tab === TabFilter.MY) {
            return translate("keyword.groups.noData.personal.description");
        }

        return translate("keyword.groups.noData.shared.description");
    };
    return (
        <FlexColumn
            alignItems="center"
            gap="8px"
        >
            <Text fontVariant="subtitle1">{getHeaderText()}</Text>
            <Text fontVariant="body2">{getDescriptionText()}</Text>
            <NotFoundImage />
        </FlexColumn>
    );
};

export const NoKeywordGroupsContainer = styled(FlexColumn)`
    width: 571px;
    gap: 60px;
    align-items: center;
    margin: 0 auto;
`;

export const MonitorKeywordGroupsTableWrapper = styled.div`
    .ant-table {
        tbody {
            tr {
                cursor: pointer;
            }
        }

        .SWReactIcons {
            svg {
                path {
                    fill: ${carbon[300]};
                    fill-opacity: 1;
                }
            }
        }
    }
`;

export const FlexTextEllipsis = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const DropdownItemStyled = styled(DropdownItem)`
    ${setFontVariant("input2", carbon[500])}
    background-color: transparent;
`;
